import {
  BootstrapPrincipalRequestSchema,
  ClientMessageBodySchema,
  CredentialsResponseSchema,
  DrainMachineRequestSchema,
  HttpErrorSchema,
  MAX_DOC_UPDATE_BYTES,
  MachineDrainStatusSchema,
  MachinesResponseSchema,
  MintTokenRequestSchema,
  PROTOCOL_VERSION,
  AttendanceResponseSchema,
  ContainerResponseSchema,
  ContainerTerminalsResponseSchema,
  IndexResponseSchema,
  PlaceRequestSchema,
  PlaceResponseSchema,
  PrincipalAccessPauseRequestSchema,
  PrincipalAccessPauseResultSchema,
  RevokeMachineRequestSchema,
  RevokeRequestSchema,
  RevokeResultSchema,
  ResolveResponseSchema,
  TerminalsResponseSchema,
  TokenGrantSchema,
  bytesToBase64,
  textToBase64,
  placementContainerFor,
  placementRefusalRule,
  topicMatches,
  type ActionDenial,
  type ActionOutcome,
  type BootstrapPrincipalRequest,
  type Cap,
  type ClientMessageBody,
  type CredentialsResponse,
  type Gesture,
  type MachineDrainStatus,
  type MachineSummary,
  type MintTokenRequest,
  type Container,
  type Attendance,
  type ContainerTerminalSummary,
  type IndexEntry,
  type ManifoldRef,
  type PlaceResponse,
  type PlacementDenial,
  type PlacementDestination,
  type PlacementRef,
  type PluginRoster,
  type PresencePayload,
  type PresenceState,
  type Principal,
  type PrincipalAccessPauseResult,
  type RevokeResult,
  type ResolveResponse,
  type SceneElement,
  type ServerEvent,
  type ServerMessageBody,
  type TerminalEnv,
  type TerminalSizing,
  type TerminalInfo,
  type TerminalProgram,
  type TerminalRuntime,
  type TerminalSummary,
  type TileLayout,
  type TokenGrant,
} from "@manifold/protocol";
import {
  LOCAL_ORIGIN,
  Y,
  REMOTE_ORIGIN,
  changedElementIds,
  createSceneDoc,
  decodeUpdate,
  elementText as sceneElementText,
  elementsMap,
  encodeUpdate,
  layoutMap,
  nextZIndex,
  patchElement,
  readElement,
  readTileLayout,
  removeElement,
  setTileRatios as sceneSetTileRatios,
  writeElement,
  type ScenePatch,
} from "@manifold/scene";
import { invokeAction } from "./action-http.ts";
import {
  acquireChannel,
  acquireObserver,
  type ChannelFrame,
  type ConnectionFrame,
  type JoinBody,
  type PooledChannel,
  type PooledSession,
} from "./connection-pool.ts";
import { type OpenStreamOptions, type StreamHandle } from "./stream.ts";

function isPooledChannel(session: PooledSession): session is PooledChannel {
  return "id" in session && "send" in session;
}

/**
 * THE session client. Browsers, tests, and tools all speak to the server through this state
 * machine — never through a second WebSocket implementation (docs/CONTRACTS.md §Protocol and
 * compatibility). With a container id it owns everything a ROOM means: join, epoch/rev
 * tracking, reconciliation and terminal subscriptions. With `containerId: null` it observes
 * workspace connection frames and events without manufacturing room state or authority.
 *
 * Since v12 the socket is multiplexed and pooled by (url, token). A canvas plus five portals
 * therefore holds one connection with six channels; a workspace root holds one roomless
 * observer on that same transport.
 *
 * Some of its ref is WORKSPACE-level rather than room-level: the action door (`action`), the
 * workspace reads (`machines`, `index`, `attendance`, `terminalsByContainer`, `terminals`), and
 * the plugin roster (`onPlugins`). A plugin holds only this client, so those questions arrive
 * through the same handle — over HTTP and the connection-level frame category, never through a
 * second roster state machine.
 */

export type ConnectionStatus = "idle" | "connecting" | "open" | "reconnecting" | "closed";

const MAX_SESSION_FAILURE_REASON_CHARS = 120;

/**
 * Consumer-visible session failure with the server socket and room channel correlation.
 * The reason is bounded before it reaches a sticky browser notice; credentials and payloads
 * never enter this object.
 */
export class SessionConnectionError extends Error {
  override readonly name = "SessionConnectionError";
  readonly reason: string;

  constructor(
    readonly connectionId: string | null,
    readonly channelId: string | null,
    readonly code: number,
    reason: string,
  ) {
    const trimmed = reason.trim();
    const bounded = (trimmed === "" ? "transport closed" : trimmed).slice(
      0,
      MAX_SESSION_FAILURE_REASON_CHARS,
    );
    const connection = connectionId ?? "pending";
    const channel = channelId ?? "unassigned";
    super(`session ${connection}/${channel} closed (${String(code)}: ${bounded})`);
    this.reason = bounded;
  }
}

/**
 * Subscribers see channel-agnostic BODIES: a handle already knows which room it is, so
 * making every listener carry a routing id would be noise. Wire frames satisfy these
 * types structurally, which is why nothing downstream had to change for v12.
 */
type ServerMessageOf<T extends ServerMessageBody["type"]> = Extract<ServerMessageBody, { type: T }>;

export interface SceneTx {
  /**
   * Authors one element. `collaborative` names the PAYLOAD fields the document should hold as
   * shared text rather than as plain values, and the author is the only party that knows: the
   * protocol's element schema is a neutral envelope (ADR 0013 §16), so the SDK carries a record
   * it does not interpret. Omitting it means "this record has no collaborative field", which is
   * the truth for every kind but one.
   */
  create(element: SceneElement, collaborative?: readonly string[]): void;
  patch(id: string, patch: ScenePatch): boolean;
  remove(id: string): boolean;
  text(id: string): Y.Text | null;
  nextZIndex(): number;
}

export interface SessionEvents {
  /** Server messages, by type. */
  message: (msg: ServerMessageBody) => void;
  status: (status: ConnectionStatus) => void;
  /** Scene document was replaced after an epoch change or full state adoption. */
  scene_reset: () => void;
  /** Validated element projections changed inside the Yjs document. */
  elements_changed: (ids: readonly string[], origin: "local" | "remote" | "undo") => void;
  /**
   * A composition's layout tree changed. The tree is small and read whole, so
   * subscribers re-read `layout()` rather than diffing tile ids.
   */
  layout_changed: (origin: "local" | "remote" | "undo") => void;
  attendance_changed: () => void;
  terminals_changed: () => void;
  /**
   * The workspace's plugin roster arrived (socket open) or changed (an enable/disable).
   * It is connection-level news, not room traffic, so it never reaches `message`.
   * Subscribe through `onPlugins`, which replays the last one it heard. `developerMode` is
   * the switch published beside the roster (`PluginsResponseSchema`); a hub that says
   * nothing about it is off.
   */
  plugins_changed: (roster: PluginRoster, developerMode: boolean) => void;
  /** Current coarse workspace authority changed, or its physical connection retired. */
  authority_changed: () => void;
}

/**
 * One live subscription on this handle: the nodes it named, the handler that hears them, and
 * the pool-level refcount release — null while this handle holds no pooled session ref,
 * because a subscription outlives the sockets that carry it while a refcount cannot.
 */
interface TopicSubscription {
  readonly topics: readonly ManifoldRef[];
  readonly handler: (event: ServerEvent) => void;
  release: (() => void) | null;
}

interface WorkspaceAuthorityState {
  readonly workspaceCaps: readonly Cap[];
  readonly workspaceEvents: boolean;
}

const NO_WORKSPACE_CAPS: readonly Cap[] = [];

type EventKey = ChannelFrame["type"] | keyof SessionEvents;
type Handler = (...args: never[]) => void;

/**
 * What `place()` answers: the placement it executed, or the declared RULE that refused
 * it. A refusal is data — never an exception — because a client renders it.
 */
export type PlaceOutcome =
  | { readonly ok: true; readonly result: PlaceResponse }
  | { readonly ok: false; readonly denial: PlacementDenial };

/**
 * What an access door answers: the thing it issued, or the denial that refused it. Same
 * shape as `PlaceOutcome` and for the same reason — a caller renders a refusal, and a
 * refused delegation is an answer rather than a broken call.
 */
export type AccessOutcome<T> =
  { readonly ok: true; readonly result: T } | { readonly ok: false; readonly denial: ActionDenial };

export interface SessionClientOptions {
  /** ws(s) URL of the session endpoint, e.g. ws://localhost:7777/ws/session */
  url: string;
  /** Room to join, or null for an authenticated workspace observer with no room membership. */
  containerId: string | null;
  token: string;
  /**
   * Joins as a spectator: this channel watches the room (state, doc updates, terminal
   * output) without occupying it. It is absent from the roster and from container presence,
   * and the server rejects any write it sends. Live previews of a container use it;
   * anything a user acts in does not.
   */
  spectator?: boolean;
  /**
   * Reconnect on unexpected close (default true). Reconnect belongs to the shared
   * CONNECTION, so the first client to open a given (url, token) transport sets it.
   */
  reconnect?: boolean;
  /**
   * DI call ref for tests. It is also part of the pool key: two clients sharing a factory
   * (and url and token) share one socket, while a test handing each client its own
   * socket double keeps them genuinely separate.
   */
  webSocketFactory?: (url: string) => WebSocket;
  /** Backoff schedule cap in ms (default 8000); a connection-level policy. */
  backoffCapMs?: number;
  /**
   * HTTP origin for placement writes; defaults to the origin `url` implies (same host,
   * ws(s) mapped to http(s)), which is what a same-origin deployment wants.
   */
  apiUrl?: string;
}

const OUTBOX_LIMIT = 256;

export class SessionClient {
  readonly attendance = new Map<string, PresenceState>();
  readonly terminals = new Map<string, TerminalInfo>();
  /** Transient sizing attribution; never persisted with a terminal or scene document. */
  readonly terminalSizing = new Map<string, TerminalSizing>();
  private readonly elementsState = new Map<string, SceneElement>();
  readonly elements: ReadonlyMap<string, SceneElement> = this.elementsState;
  /** Held view refcounts per terminal, by exact `viewportId` (see attachTerminal). */
  private readonly attachCounts = new Map<string, Map<string, number>>();
  epoch = "";
  rev = 0;
  self: Principal | null = null;
  selfConnId: string | null = null;
  private selfCapsState: readonly Cap[] = [];
  private workspaceAuthority: WorkspaceAuthorityState | null = null;
  private readonly subscriptionSyncWaits = new Set<() => void>();
  status: ConnectionStatus = "idle";
  private connectionIdState: string | null = null;

  private readonly opts: Required<Pick<SessionClientOptions, "url" | "containerId" | "token">> &
    SessionClientOptions;
  /** This handle's room channel or roomless observer; null before connect and after close. */
  private channel: PooledSession | null = null;
  private attachmentEpoch = 0;
  private listeners = new Map<EventKey, Set<Handler>>();
  private outbox: ClientMessageBody[] = [];
  private closeError: SessionConnectionError | null = null;
  /**
   * The last plugin roster this connection delivered, with the developer-mode switch that
   * rode beside it, replayed to every late `onPlugins` subscriber. A roster is workspace
   * state, so the newest one is the whole truth.
   */
  private pluginRoster: { readonly roster: PluginRoster; readonly developerMode: boolean } | null =
    null;
  /**
   * Live event subscriptions. A Set rather than a keyed table: two callers may name the same
   * node for different reasons, and each holds its own release — so identity is the record
   * itself, never the topics it happens to share.
   */
  private readonly subscriptions = new Set<TopicSubscription>();
  private readonly streams = new Set<StreamHandle>();
  private currentDoc = createSceneDoc();
  private undoManager!: Y.UndoManager;
  private hasLocalEdits = false;

  constructor(opts: SessionClientOptions) {
    this.opts = opts;
    this.installDoc(this.currentDoc);
  }

  /**
   * The pooled connection carrying this room, or null before `connect()`. Two handles
   * reporting the same id ARE sharing one socket — the multiplex invariant, observable
   * without reaching into the transport.
   */
  get transportId(): string | null {
    return this.channel?.transportId ?? null;
  }

  /** This room's channel id, or null for a roomless observer. */
  get channelId(): string | null {
    return this.channel !== null && isPooledChannel(this.channel) ? this.channel.id : null;
  }

  /** Server-issued correlation for the current socket; null until its first frame arrives. */
  get connectionId(): string | null {
    return this.connectionIdState;
  }

  /** Last terminal connection failure, retained so a mounted view can render diagnostics. */
  get connectionError(): SessionConnectionError | null {
    return this.closeError;
  }

  /**
   * The joining principal's granted caps, as the last init/resync reported them — empty
   * until the first one lands. A method rather than a field because it is an ANSWER a
   * caller may hold across reconnects (a plugin gating its affordances asks again), and
   * because the plugin engine's `SessionHandle` is a method-only ref.
   */
  selfCaps(): readonly Cap[] {
    return this.selfCapsState;
  }

  /** Effective caps at the workspace root for this physical connection's credential. */
  workspaceCaps(): readonly Cap[] {
    return this.workspaceAuthority?.workspaceCaps ?? NO_WORKSPACE_CAPS;
  }

  /** A coarse event-plane hint, never a per-topic admission or action authorization. */
  workspaceEventsAvailable(): boolean {
    return this.workspaceAuthority?.workspaceEvents ?? false;
  }

  onAuthorityChange(callback: () => void): () => void {
    return this.on("authority_changed", callback);
  }

  /** Orders declarations before a catch-up read; false preserves the caller's polling. */
  syncSubscriptions(): Promise<boolean> {
    const channel = this.channel;
    const authority = this.workspaceAuthority;
    if (channel === null || this.status !== "open" || authority === null) {
      return Promise.resolve(false);
    }
    return new Promise<boolean>((resolve) => {
      const retire = (): void => {
        this.subscriptionSyncWaits.delete(retire);
        resolve(false);
      };
      this.subscriptionSyncWaits.add(retire);
      void channel.syncSubscriptions().then((synchronized) => {
        this.subscriptionSyncWaits.delete(retire);
        resolve(
          synchronized &&
            this.channel === channel &&
            this.status === "open" &&
            this.workspaceAuthority === authority,
        );
      });
    });
  }

  private retireSubscriptionSyncWaits(): void {
    for (const retire of this.subscriptionSyncWaits) retire();
  }

  private updateWorkspaceAuthority(authority: WorkspaceAuthorityState | null): void {
    const prior = this.workspaceAuthority;
    if (
      prior === authority ||
      (prior !== null &&
        authority !== null &&
        prior.workspaceEvents === authority.workspaceEvents &&
        prior.workspaceCaps.length === authority.workspaceCaps.length &&
        prior.workspaceCaps.every((cap, index) => cap === authority.workspaceCaps[index]))
    ) {
      return;
    }
    this.retireSubscriptionSyncWaits();
    this.workspaceAuthority = authority;
    this.emit("authority_changed");
  }

  private installDoc(doc: Y.Doc): void {
    const map = elementsMap(doc);
    this.undoManager = new Y.UndoManager(map, {
      trackedOrigins: new Set([LOCAL_ORIGIN]),
      captureTimeout: 400,
    });
    map.observeDeep((events, transaction) => {
      const ids = changedElementIds(events as unknown as readonly Y.YEvent<never>[]);
      for (const id of ids) {
        const element = readElement(doc, id);
        if (element === null) this.elementsState.delete(id);
        else this.elementsState.set(id, element);
      }
      if (ids.length > 0) this.emit("elements_changed", ids, this.classifyOrigin(transaction));
    });
    // Canvas containers never write tiles, so this observer stays silent for them and
    // a composition needs no second subscription path.
    layoutMap(doc).observeDeep((_events, transaction) => {
      this.emit("layout_changed", this.classifyOrigin(transaction));
    });
    doc.on("update", (update, origin) => {
      if (origin === REMOTE_ORIGIN) return;
      this.hasLocalEdits = true;
      this.send({ type: "doc_update", update: encodeUpdate(update) });
    });
  }

  /** Both projection observers report provenance the same way. */
  private classifyOrigin(transaction: Y.Transaction): "local" | "remote" | "undo" {
    if (transaction.origin === LOCAL_ORIGIN) return "local";
    if (transaction.origin === this.undoManager) return "undo";
    return "remote";
  }

  private replaceDoc(): void {
    this.currentDoc.destroy();
    this.elementsState.clear();
    this.currentDoc = createSceneDoc();
    this.installDoc(this.currentDoc);
  }

  // ------------------------------------------------------------------ events

  on<T extends ServerMessageBody["type"]>(
    type: T,
    fn: (msg: ServerMessageOf<T>) => void,
  ): () => void;
  on<K extends keyof SessionEvents>(type: K, fn: SessionEvents[K]): () => void;
  on(type: EventKey, fn: Handler): () => void {
    let set = this.listeners.get(type);
    if (!set) {
      set = new Set();
      this.listeners.set(type, set);
    }
    set.add(fn);
    return () => set.delete(fn);
  }

  /**
   * One listener's failure must never starve its siblings: document state advances
   * before projection events are emitted. Errors remain observable without propagating
   * into the Yjs transaction or starving later listeners.
   */
  private emit(type: EventKey, ...args: unknown[]): void {
    const set = this.listeners.get(type);
    if (!set) return;
    for (const fn of [...set]) {
      try {
        (fn as (...a: unknown[]) => void)(...args);
      } catch (error) {
        console.error("evt=terminal_listener_failed", type, error);
      }
    }
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    if (status !== "open" && this.terminalSizing.size > 0) {
      this.terminalSizing.clear();
      this.emit("terminals_changed");
    }
    if (status !== "open") this.retireSubscriptionSyncWaits();
    this.emit("status", status);
  }

  // ------------------------------------------------------------------ lifecycle

  /**
   * Resolves on the first successful room init or observer admission; reconnects continue
   * afterwards. Calling it on a live handle explicitly redials the shared transport.
   */
  connect(): Promise<void> {
    this.closeError = null;
    const { promise, resolve, reject } = Promise.withResolvers<void>();
    const offStatus = this.on("status", (s: ConnectionStatus) => {
      // Room init/resync and observer acknowledgement both establish this handle.
      if (s === "open") {
        offStatus();
        resolve();
      } else if (s === "closed") {
        offStatus();
        reject(
          this.closeError ??
            new Error(
              this.opts.containerId === null
                ? "observer closed before admission"
                : "channel closed before initialization",
            ),
        );
      }
    });
    if (this.channel === null) this.attach();
    else this.channel.redial();
    return promise;
  }

  /** Opens a bounded continuous stream on this session's existing pooled transport. */
  openStream(options: OpenStreamOptions): StreamHandle {
    if (this.channel === null) this.attach();
    const pooled = this.channel!.openStream(options);
    const handle: StreamHandle = {
      get snapshot() {
        return pooled.snapshot;
      },
      get cursor() {
        return pooled.cursor;
      },
      get status() {
        return pooled.status;
      },
      on: (listener) => pooled.on(listener),
      close: () => {
        this.streams.delete(handle);
        pooled.close();
      },
    };
    this.streams.add(handle);
    return handle;
  }

  close(): void {
    const retiredEpoch = ++this.attachmentEpoch;
    this.closeError = null;
    const channel = this.channel;
    this.channel = null;
    // Subscriptions are refcounted on the SOCKET, which may outlive this room: dropping this
    // handle's share is what keeps a sibling's subscription alive and this one's from leaking.
    for (const record of this.subscriptions) {
      record.release?.();
      record.release = null;
    }
    for (const stream of this.streams) stream.close();
    // Releasing drops this room or observer and closes the socket only when no pooled handle remains.
    channel?.release();
    this.setStatus("closed");
    if (this.attachmentEpoch === retiredEpoch) this.updateWorkspaceAuthority(null);
  }

  /** The join THIS room wants, rebuilt per attempt so resume hints are current. */
  private joinBody(): JoinBody {
    const containerId = this.opts.containerId;
    if (containerId === null) throw new Error("roomless observer has no join body");
    return {
      type: "join",
      containerId,
      token: this.opts.token,
      protocolVersion: PROTOCOL_VERSION,
      ...(this.opts.spectator === true ? { spectator: true } : {}),
      ...(this.epoch !== "" ? { lastEpoch: this.epoch, lastRev: this.rev } : {}),
    };
  }

  /** Acquires this handle on the pooled connection its (url, token) names. */
  private attach(): void {
    const attachmentEpoch = ++this.attachmentEpoch;
    const options = {
      url: this.opts.url,
      token: this.opts.token,
      ...(this.opts.reconnect !== undefined ? { reconnect: this.opts.reconnect } : {}),
      ...(this.opts.backoffCapMs !== undefined ? { backoffCapMs: this.opts.backoffCapMs } : {}),
      ...(this.opts.webSocketFactory !== undefined
        ? { webSocketFactory: this.opts.webSocketFactory }
        : {}),
    };
    const connectionFrame = (body: ConnectionFrame): void => {
      if (this.attachmentEpoch === attachmentEpoch) this.handleConnection(body);
    };
    const transportPhase = (phase: ConnectionStatus): void => {
      if (this.attachmentEpoch !== attachmentEpoch) return;
      this.connectionIdState = null;
      this.setStatus(phase);
      if (this.attachmentEpoch === attachmentEpoch) this.updateWorkspaceAuthority(null);
    };
    const transportClosed = (
      failure: { readonly code: number; readonly reason: string } | null,
    ): void => {
      if (this.attachmentEpoch !== attachmentEpoch) return;
      const retiredEpoch = ++this.attachmentEpoch;
      const channelId = this.channelId;
      this.forgetSubscriptions();
      this.channel = null;
      this.closeError =
        failure === null
          ? null
          : new SessionConnectionError(
              this.connectionIdState,
              channelId,
              failure.code,
              failure.reason,
            );
      this.setStatus("closed");
      if (this.attachmentEpoch === retiredEpoch) this.updateWorkspaceAuthority(null);
    };
    const channel =
      this.opts.containerId === null
        ? acquireObserver(options, {
            connectionFrame,
            transportPhase,
            observed: () => {
              if (this.attachmentEpoch === attachmentEpoch && this.channel?.isOpen()) {
                this.setStatus("open");
              }
            },
            transportClosed,
          })
        : acquireChannel(options, {
            joinBody: () => this.joinBody(),
            receive: (body) => {
              if (this.attachmentEpoch === attachmentEpoch) this.handle(body);
            },
            connectionFrame,
            transportPhase,
            channelClosed: (code, reason, terminal) => {
              if (this.attachmentEpoch !== attachmentEpoch) return;
              if (!terminal) {
                this.setStatus("reconnecting");
                return;
              }
              const retiredEpoch = ++this.attachmentEpoch;
              const channelId = this.channelId;
              this.releaseSubscriptions();
              this.channel = null;
              this.closeError = new SessionConnectionError(
                this.connectionIdState,
                channelId,
                code,
                reason,
              );
              this.setStatus("closed");
              if (this.attachmentEpoch === retiredEpoch) this.updateWorkspaceAuthority(null);
            },
            transportClosed,
          });
    // Replayed state can synchronously close or replace this handle before acquisition returns.
    if (this.attachmentEpoch !== attachmentEpoch) {
      channel.release();
      return;
    }
    this.channel = channel;
    for (const record of this.subscriptions) record.release = channel.subscribe(record.topics);
  }

  /**
   * Withdraws this handle's refcounts ON THE WIRE and then drops them. The per-channel
   * terminal close is exactly the case where the release closures still address something:
   * the socket survives, other rooms keep using it, and this room's increments are the only
   * thing standing between their topics and the 1→0 that emits `unsubscribe`. Discarding the
   * closures here would strand those increments forever — a multiplexed socket permanently
   * over-subscribed, with no later `off()` able to reach the withdrawal it was promised.
   *
   * The subscriptions themselves survive: `attach` re-declares them on the next channel.
   */
  private releaseSubscriptions(): void {
    for (const stream of this.streams) stream.close();
    for (const record of this.subscriptions) {
      record.release?.();
      record.release = null;
    }
  }

  /**
   * Drops this handle's refcounts WITHOUT withdrawing them on the wire: the connection they
   * were counted on is gone, so there is nothing to tell and nobody to tell it. That premise
   * holds for the TRANSPORT close alone, which is why the per-channel terminal path releases
   * instead. The subscriptions themselves survive — `attach` re-declares them on the next
   * socket, which is what makes a subscription outlive a transport it never knew about.
   */
  private forgetSubscriptions(): void {
    for (const stream of this.streams) stream.close();
    for (const record of this.subscriptions) record.release = null;
  }

  // ------------------------------------------------------------------ incoming

  /**
   * A frame addressed to the SOCKET this room rides. It never touches epoch/rev, never
   * queues, and never reaches `message`: a roster describes the workspace and an event
   * describes a node, neither of which is this room's traffic, and every handle on the
   * connection hears the same one.
   */
  private handleConnection(body: ConnectionFrame): void {
    switch (body.type) {
      case "session":
        this.connectionIdState = body.connectionId;
        return;
      case "plugins": {
        const developerMode = body.developerMode === true;
        this.pluginRoster = { roster: body.roster, developerMode };
        this.emit("plugins_changed", body.roster, developerMode);
        return;
      }
      case "authority_context":
        this.updateWorkspaceAuthority(body);
        return;
      case "event":
        this.deliverEvent(body);
        return;
      default: {
        const exhaustive: never = body;
        return exhaustive;
      }
    }
  }

  /**
   * Hands one event to the subscriptions that named its node. The match is
   * `topicMatches` — the protocol's own relation, the same one the server fans out by — so a
   * frame this socket was sent can never fail to find the handler that asked for it.
   *
   * A subscription that named several topics still hears one delivery per event: a handler
   * asked about a set of nodes, not about a set of matches.
   */
  private deliverEvent(event: ServerEvent): void {
    for (const record of [...this.subscriptions]) {
      if (!record.topics.some((topic) => topicMatches(topic, event.topic))) continue;
      try {
        record.handler(event);
      } catch (error) {
        console.error("evt=terminal_listener_failed", "event", error);
      }
    }
  }

  /**
   * THE event-plane door: declares interest in a set of nodes and answers the release.
   *
   * Topics are REFS, never `manifold://` strings — the address is compiler-joined, which is
   * why the topic namespace needs no registry (`REGISTRY.md` §Runtime-joined namespaces). What
   * arrives is a notification: a subscriber that needs the new state READS it, through the
   * same door a fresh client uses, because there is no offset to resume from and nothing is
   * replayed. That is the whole contract, and it is why a feed can trade its timer for a
   * subscription without changing what it does when it wakes up.
   * The frame's `plugin` is the origin, independent of its topic. A handler selecting an
   * event kind must qualify it by `plugin`, since different plugins may declare the same kind.
   *
   * The wire declaration is refcounted onto the SOCKET one layer down, so two panels watching
   * one container cost one `subscribe`, and neither can cancel the other by releasing first.
   * Subscribing before `connect()` is legal: the declaration goes out with the join.
   */
  subscribe(topics: readonly ManifoldRef[], handler: (event: ServerEvent) => void): () => void {
    const record: TopicSubscription = { topics: [...topics], handler, release: null };
    this.subscriptions.add(record);
    if (this.channel !== null) record.release = this.channel.subscribe(record.topics);
    return () => {
      if (!this.subscriptions.delete(record)) return;
      record.release?.();
      record.release = null;
    };
  }

  /**
   * Subscribes to the workspace's plugin roster and REPLAYS the last one synchronously,
   * so a host mounting after the socket opened renders a composition immediately instead
   * of waiting for the next enable/disable. Returns the unsubscribe.
   */
  onPlugins(fn: (roster: PluginRoster, developerMode: boolean) => void): () => void {
    const off = this.on("plugins_changed", fn);
    if (this.pluginRoster !== null) fn(this.pluginRoster.roster, this.pluginRoster.developerMode);
    return off;
  }

  private handle(msg: ChannelFrame): void {
    switch (msg.type) {
      case "init":
      case "resync": {
        const previousSelfConnId = this.selfConnId;
        const lineageChanged = this.epoch !== "" && this.epoch !== msg.epoch;
        if (lineageChanged) {
          this.outbox = this.outbox.filter((queued) => queued.type !== "doc_update");
          this.replaceDoc();
        }
        Y.applyUpdate(this.currentDoc, decodeUpdate(msg.doc), REMOTE_ORIGIN);
        this.epoch = msg.epoch;
        this.rev = msg.rev;
        this.self = msg.self;
        this.selfConnId = msg.selfConnId;
        this.selfCapsState = msg.selfCaps;
        this.attendance.clear();
        for (const p of msg.attendance) this.attendance.set(p.principal.id, p);
        this.terminals.clear();
        for (const s of msg.terminals) this.terminals.set(s.id, s);
        for (const terminalId of this.terminalSizing.keys()) {
          if (this.terminals.get(terminalId)?.status !== "running") {
            this.terminalSizing.delete(terminalId);
          }
        }
        this.setStatus("open");
        // Liveness belongs to the socket, which the pooled connection owns and answers for.
        this.flushOutbox();
        if (this.hasLocalEdits) {
          const state = Y.encodeStateAsUpdate(this.currentDoc);
          if (state.byteLength <= MAX_DOC_UPDATE_BYTES) {
            this.send({ type: "doc_update", update: encodeUpdate(state) });
          } else {
            console.error(
              "evt=doc_state_too_large",
              `bytes=${state.byteLength}`,
              `limit=${MAX_DOC_UPDATE_BYTES}`,
            );
          }
        }
        // Re-subscribe views only after a real reconnect: the server's viewer registry is
        // connection-scoped, while a same-connection resync preserves those subscriptions.
        // Every held pair attaches again; each answers with its own fresh incarnation.
        if (previousSelfConnId !== null && previousSelfConnId !== msg.selfConnId) {
          for (const [attachedId, views] of this.attachCounts) {
            if (this.terminals.get(attachedId)?.status !== "running") continue;
            for (const viewportId of views.keys()) {
              this.send({ type: "terminal_attach", terminalId: attachedId, viewportId });
            }
          }
        }
        this.emit(msg.type, msg);
        this.emit("scene_reset");
        this.emit("attendance_changed");
        this.emit("terminals_changed");
        break;
      }
      case "doc_update": {
        Y.applyUpdate(this.currentDoc, decodeUpdate(msg.update), REMOTE_ORIGIN);
        this.emit(msg.type, msg);
        break;
      }
      case "attendance": {
        if (msg.joined) this.attendance.set(msg.joined.principal.id, msg.joined);
        if (msg.left) this.attendance.delete(msg.left.principalId);
        this.emit(msg.type, msg);
        this.emit("attendance_changed");
        break;
      }
      case "presence": {
        const entry = this.attendance.get(msg.principalId);
        if (entry) {
          // Location belongs to the stamped connection, never the principal's last-writer payload.
          const connectionLocations =
            msg.payload.vantage === undefined
              ? entry.connectionLocations
              : entry.connIds.map((connId) => ({
                  connId,
                  locationPath:
                    connId === msg.connId
                      ? (msg.payload.vantage?.locationPath ?? null)
                      : (entry.connectionLocations?.find((location) => location.connId === connId)
                          ?.locationPath ?? null),
                }));
          this.attendance.set(msg.principalId, {
            ...entry,
            payload: { ...entry.payload, ...msg.payload },
            ...(connectionLocations === undefined ? {} : { connectionLocations }),
          });
        }
        this.emit(msg.type, msg);
        this.emit("attendance_changed");
        break;
      }
      case "terminal_opened": {
        this.terminals.set(msg.terminal.id, msg.terminal);
        this.emit(msg.type, msg);
        this.emit("terminals_changed");
        break;
      }
      case "terminal_event": {
        if (msg.kind === "parked") {
          // A departure notice: the terminal is no longer in THIS room, so it is no longer
          // reachable over this channel. It either re-homed into another composition (a
          // merge or an extraction) or it was killed and left every room.
          this.terminals.delete(msg.terminalId);
          this.terminalSizing.delete(msg.terminalId);
          this.emit(msg.type, msg);
          this.emit("terminals_changed");
          break;
        }
        const terminal = this.terminals.get(msg.terminalId);
        if (terminal) {
          const next: TerminalInfo = { ...terminal };
          if (msg.kind === "exited") {
            next.status = "exited";
            this.terminalSizing.delete(msg.terminalId);
            next.exitCode = msg.exitCode ?? null;
            next.exitReason = msg.exitReason ?? null;
          }
          if (msg.kind === "controller_changed") next.controllerId = msg.controllerId ?? null;
          if (msg.kind === "resized") {
            if (msg.cols !== undefined) next.cols = msg.cols;
            if (msg.rows !== undefined) next.rows = msg.rows;
          }
          if (msg.kind === "renamed") next.name = msg.name ?? null;
          if (msg.kind === "ready" && msg.readiness !== undefined) {
            next.readiness = msg.readiness;
          }
          if (msg.kind === "cwd" || msg.kind === "restarted") {
            if (msg.cwd !== undefined) next.cwd = msg.cwd;
          }
          if (msg.kind === "restarted") {
            next.status = "running";
            next.exitCode = null;
            next.exitReason = null;
            next.readiness = null;
            this.terminalSizing.delete(msg.terminalId);
            next.controllerId = msg.controllerId ?? null;
          }
          this.terminals.set(msg.terminalId, next);
        }
        this.emit(msg.type, msg);
        this.emit("terminals_changed");
        const restartedViews =
          msg.kind === "restarted" ? this.attachCounts.get(msg.terminalId) : undefined;
        // Exit detaches the server's viewers, not our mounted refs. Notify every local view
        // before acquiring each held view's fresh snapshot of the replacement stream.
        for (const viewportId of restartedViews?.keys() ?? []) {
          this.send({ type: "terminal_attach", terminalId: msg.terminalId, viewportId });
        }
        break;
      }
      case "terminal_sizing": {
        if (this.terminals.has(msg.terminalId)) {
          this.terminalSizing.set(msg.terminalId, msg.sizing);
          this.emit("terminals_changed");
        }
        this.emit(msg.type, msg);
        break;
      }
      case "error": {
        this.emit(msg.type, msg);
        break;
      }
      case "cursor":
      case "gesture":
      case "terminal_snapshot":
      case "terminal_output":
      case "terminal_geometry":
      case "terminal_delivery": {
        this.emit(msg.type, msg);
        break;
      }
      case "saved": {
        this.rev = Math.max(this.rev, msg.rev);
        this.emit(msg.type, msg);
        break;
      }
      default: {
        const _exhaustive: never = msg;
        void _exhaustive;
      }
    }
    this.emit("message", msg);
  }

  // ------------------------------------------------------------------ outgoing

  private send(msg: ClientMessageBody): void {
    if (this.opts.containerId === null) return;
    const channel = this.channel;
    if (
      channel !== null &&
      isPooledChannel(channel) &&
      channel.isOpen() &&
      this.status === "open"
    ) {
      // Development guard: never put an invalid frame on the wire. High-volume binary
      // frame types are constructed entirely by this SDK, so avoid rescanning their payloads.
      if (msg.type !== "cursor" && msg.type !== "doc_update" && msg.type !== "terminal_input") {
        ClientMessageBodySchema.parse(msg);
      }
      channel.send(msg);
      return;
    }
    // Connection-scoped ephemera must never replay: active views republish current intent,
    // and an acknowledgement names an incarnation no later connection can hold.
    // Liveness is not here at all — the pooled connection owns the socket's ping.
    if (
      msg.type === "cursor" ||
      msg.type === "gesture" ||
      msg.type === "terminal_resize" ||
      msg.type === "terminal_ack"
    )
      return;
    if (this.outbox.length >= OUTBOX_LIMIT) this.outbox.shift();
    this.outbox.push(msg);
  }

  private flushOutbox(): void {
    const queued = this.outbox;
    this.outbox = [];
    for (const msg of queued) this.send(msg);
  }

  transact(fn: (tx: SceneTx) => void): void {
    this.currentDoc.transact(() => {
      fn({
        create: (element, collaborative) =>
          writeElement(this.currentDoc, element, LOCAL_ORIGIN, collaborative),
        patch: (id, patch) => patchElement(this.currentDoc, id, patch, LOCAL_ORIGIN),
        remove: (id) => removeElement(this.currentDoc, id, LOCAL_ORIGIN),
        text: (id) => sceneElementText(this.currentDoc, id),
        nextZIndex: () => nextZIndex(this.currentDoc),
      });
    }, LOCAL_ORIGIN);
  }

  undo(): void {
    this.undoManager.undo();
  }

  redo(): void {
    this.undoManager.redo();
  }

  elementText(id: string): Y.Text | null {
    return sceneElementText(this.currentDoc, id);
  }

  /**
   * The composition's node table, or null for a canvas — and also for a tree that
   * fails validation, including one that tiles this very container: an unusable tree is
   * never handed to the renderer. Read whole on every `layout_changed`.
   */
  layout(): TileLayout | null {
    return this.opts.containerId === null
      ? null
      : readTileLayout(this.currentDoc, this.opts.containerId);
  }

  /**
   * The only layout mutation the SDK owns: a divider drag is high-frequency and purely
   * geometric. Structural writes (splits, removals, extraction) stay on HTTP so the
   * server can enforce discipline and the composition lifecycle — a leaf that leaves
   * re-homes its terminal, and a composition emptied by the move is retired.
   */
  setTileRatios(splitId: string, ratios: readonly number[]): void {
    sceneSetTileRatios(this.currentDoc, splitId, ratios, LOCAL_ORIGIN);
  }

  /**
   * THE placement call: put an item in a container. One envelope, one verb, and a refusal
   * that names the RULE which refused it — legality lives in the protocol's placement
   * declarations, so a caller never has to know which verb this used to be.
   *
   * It dispatches `core.space.place` (ADR 0013 §14): the algebra is mechanism and stays
   * floor, the verb is a plugin, and there is exactly one door onto "place a thing". This
   * method survives the move because the SHAPE is the contract — callers keep asking for a
   * ref and a destination and keep getting a `PlaceOutcome`.
   *
   * The denial is REBUILT rather than received: the action door's `refused` rung carries one
   * string, which leads with the algebra's own rule, and the two other fields of a
   * `PlacementDenial` are things this caller already holds — it sent the ref, and the
   * container is a total function of the destination. Anything that is NOT a placement rule
   * (a cap the caller lacks, a disabled plugin, a placement that could not be carried out)
   * throws, exactly as the HTTP failures it replaces did.
   */
  async place(ref: PlacementRef, destination: PlacementDestination): Promise<PlaceOutcome> {
    const request = PlaceRequestSchema.parse({ ref, destination });
    const outcome = await this.action("core.space.place", request);
    if (outcome.ok) return { ok: true, result: PlaceResponseSchema.parse(outcome.result) };
    const rule = placementRefusalRule(outcome.denial.message);
    if (rule === null) throw new Error(outcome.denial.message);
    // A denial is an ANSWER, not a failure: the caller renders the rule (and its drag
    // preview already asked `resolvePlacement` the same question locally).
    return {
      ok: false,
      denial: { rule, ref, container: placementContainerFor(destination) },
    };
  }

  /**
   * THE action call: invoke a plugin action by its full `plugin.local` name. A denial is
   * DATA — the rule that refused and its message — exactly like a placement refusal,
   * because a client renders it; only a broken door (non-JSON, an error envelope) throws.
   *
   * HTTP rather than a socket frame for the same reason placement is: an action addresses
   * the WORKSPACE, while this client is joined to one room.
   */
  async action(name: string, args: unknown): Promise<ActionOutcome> {
    return (await invokeAction({ origin: this.apiOrigin(), token: this.opts.token }, name, args))
      .outcome;
  }

  /** The HTTP origin this terminal's socket URL implies; `apiUrl` overrides it. */
  private apiOrigin(): string {
    if (this.opts.apiUrl !== undefined) return this.opts.apiUrl.replace(/\/+$/, "");
    const url = new URL(this.opts.url);
    url.protocol = url.protocol === "wss:" ? "https:" : "http:";
    return url.origin;
  }

  // ---------------------------------------------------------- workspace reads

  /*
    A plugin asks the WORKSPACE these questions — which machines exist, what the index
    holds — and it holds only this client. The reads therefore live here rather than in
    each host's own fetch layer, which is what kept them out of reach of anything but the
    web app: one door per concept.
   */

  /** One authed GET; a non-2xx is a failure, never data — these reads have no denials. */
  private async getJson(path: string): Promise<unknown> {
    const response = await fetch(`${this.apiOrigin()}${path}`, {
      headers: { authorization: `Bearer ${this.opts.token}` },
    });
    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      throw new Error(`GET ${path} returned a non-JSON response (${response.status})`);
    }
    if (response.ok) return payload;
    const failure = HttpErrorSchema.safeParse(payload);
    throw new Error(
      failure.success ? failure.data.error.message : `GET ${path} failed (${response.status})`,
    );
  }

  /** The enrolled machines with live online state (`core.machines.list`). */
  async machines(): Promise<readonly MachineSummary[]> {
    return MachinesResponseSchema.parse(await this.invoke("core.machines.list", {})).machines;
  }

  /** The workspace index — containers and folders in tree order (`core.index.read`). */
  async index(): Promise<readonly IndexEntry[]> {
    return IndexResponseSchema.parse(await this.invoke("core.index.read", {})).items;
  }

  /** Who occupies which container right now (`GET /api/attendance`). */
  async attendanceByContainer(): Promise<readonly Attendance[]> {
    return AttendanceResponseSchema.parse(await this.getJson("/api/attendance")).attendance;
  }

  /**
   * WHAT THIS ADDRESS NAMES (`GET /api/resolve`): the canonical spelling of the reference, its
   * structured form, whether the node exists, and what the workspace calls it.
   *
   * The typed half of the addressing algebra's one read door. `manifold://` is the canonical
   * reference form for anything addressable (docs/CONTRACTS.md §Reference nodes), and a caller holding a URI
   * had until now no way to ask whether it still names anything — so every consumer either
   * guessed or parsed the string itself and believed the answer. Parsing tells you the SHAPE;
   * only the server knows whether the node is there.
   *
   * Refusals are HTTP errors on purpose, matching the door: an unparseable address is a bad
   * request and throws, while an address that parses and points at nothing is a successful
   * answer carrying `exists: false`.
   */
  async resolve(uri: string): Promise<ResolveResponse> {
    return ResolveResponseSchema.parse(
      await this.getJson(`/api/resolve?uri=${encodeURIComponent(uri)}`),
    );
  }

  /** Every container-homed terminal, for per-container counts (`core.terminals.listByContainer`). */
  async terminalsByContainer(): Promise<readonly ContainerTerminalSummary[]> {
    return ContainerTerminalsResponseSchema.parse(
      await this.invoke("core.terminals.listByContainer", {}),
    ).terminals;
  }

  /** Every terminal in the workspace with its home composition (`core.terminals.listAll`). */
  async allTerminals(): Promise<readonly TerminalSummary[]> {
    return TerminalsResponseSchema.parse(await this.invoke("core.terminals.listAll", {})).terminals;
  }

  // --------------------------------------------------------- workspace writes

  /*
    The workspace index's writes, beside its reads for the same reason: the section that
    LISTS containers is the section that renames and deletes them, and it holds only this
    client. Every one of them is now a `core.index` ACTION — the bespoke routes are gone
    (D13) — and each wrapper keeps its name, its arguments and its answer, because a method
    name is a contract with plugin authors while the door behind it is ours to move.
   */

  /**
   * One action as a TYPED WRAPPER calls it: the result on success, a throw carrying the
   * denial's message on refusal. `action` itself keeps denials as DATA, which is right for a
   * caller that renders the rule; these wrappers replaced routes whose refusal was an HTTP
   * error, and every caller of theirs is written around a throw. One shape per call site,
   * chosen by the call site.
   *
   * THE WRAPPERS BELOW NAME PLUGINS FROM INSIDE A FLOOR PILLAR, and that is ratified rather
   * than tolerated (ADR 0013, addendum 2026-09-01, issue #113): a wrapper is a TYPED
   * PROJECTION of a published door, never a second door. Each dispatches through `action`
   * above rather than a transport of its own; each adds typing and result parsing only, so a
   * `plugin_disabled` refusal reaches the caller verbatim instead of being papered over; and
   * `action` stays public and complete, so a stranger's door is reachable with no wrapper at
   * all and deleting every one of these would cost reach to nobody. The `core.` in a method
   * name is AUTHORSHIP, exactly as it is on the wire — this client decides nothing
   * differently for a door it happens to have typed.
   */
  private async invoke(name: string, args: unknown): Promise<unknown> {
    const outcome = await this.action(name, args);
    if (!outcome.ok) throw new Error(outcome.denial.message);
    return outcome.result;
  }

  /** Renames one container (`core.index.renameContainer`). */
  async renameContainer(containerId: string, name: string): Promise<Container> {
    const result = await this.invoke("core.index.renameContainer", { containerId, name });
    return ContainerResponseSchema.parse(result).container;
  }

  /** Retires one container (`core.index.deleteContainer`); the door enforces root authority. */
  async deleteContainer(containerId: string): Promise<void> {
    await this.invoke("core.index.deleteContainer", { containerId });
  }

  /** One container's record (`core.index.readContainer`), for a reference the index has not answered. */
  async getContainer(containerId: string): Promise<Container> {
    const result = await this.invoke("core.index.readContainer", { containerId });
    return ContainerResponseSchema.parse(result).container;
  }

  /**
   * Removes one leaf from a composition (`core.space.removeTile`). Removal is the one tile
   * gesture that is NOT a placement — nothing accepts "nowhere" for a LEAF — so it is its own
   * door while every MOVE of a leaf's occupant goes through `place`. It was its own ROUTE until
   * issue #114: a bespoke `DELETE` that committed workspace state outside the dispatch ladder
   * and therefore left no trace row. Same wrapper, same signature, same throw-on-refusal shape
   * every caller was already written around — only the door underneath moved.
   */
  async removeContainerTile(containerId: string, tileId: string): Promise<void> {
    await this.invoke("core.space.removeTile", { containerId, tileId });
  }

  /** Creates an index folder (`core.index.createFolder`); answers the whole new index. */
  async createFolder(name: string, parentId: string | null): Promise<readonly IndexEntry[]> {
    const result = await this.invoke("core.index.createFolder", { name, parentId });
    return IndexResponseSchema.parse(result).items;
  }

  /** Renames an index folder (`core.index.renameFolder`). */
  async renameFolder(folderId: string, name: string): Promise<readonly IndexEntry[]> {
    const result = await this.invoke("core.index.renameFolder", { folderId, name });
    return IndexResponseSchema.parse(result).items;
  }

  /** Deletes an index folder (`core.index.deleteFolder`); its children move up. */
  async deleteFolder(folderId: string): Promise<readonly IndexEntry[]> {
    const result = await this.invoke("core.index.deleteFolder", { folderId });
    return IndexResponseSchema.parse(result).items;
  }

  /** Moves one index item between siblings or into a folder (`core.index.moveEntry`). */
  async moveIndexEntry(
    item: { readonly kind: "container" | "folder"; readonly id: string },
    parentId: string | null,
    index: number,
  ): Promise<readonly IndexEntry[]> {
    const result = await this.invoke("core.index.moveEntry", { item, parentId, index });
    return IndexResponseSchema.parse(result).items;
  }

  // -------------------------------------------------------------- access doors

  /*
    Handing authority out, over the same client every other capability uses (A2): a remote
    human, the browser and an agent reach `core.access` identically. Unlike the index writes
    above, a denial here is DATA rather than a throw — "you may not mint that cap" and
    "you may not widen your container scope" are answers a caller renders and often expects, so
    `invoke`'s throw-on-refusal would turn a normal negotiation into an exception.
  */

  /** One access door, with its refusal kept as data. */
  private async accessDoor<T>(
    name: string,
    args: unknown,
    parse: (result: unknown) => T,
  ): Promise<AccessOutcome<T>> {
    const outcome = await this.action(name, args);
    if (!outcome.ok) return { ok: false, denial: outcome.denial };
    return { ok: true, result: parse(outcome.result) };
  }

  /**
   * Creates a principal holding a root token (`core.access.createPrincipal`). Root-only,
   * and the workspace's bootstrap: this is how the owner key becomes a durable identity.
   */
  async createPrincipal(input: BootstrapPrincipalRequest): Promise<AccessOutcome<TokenGrant>> {
    const request = BootstrapPrincipalRequestSchema.parse(input);
    return this.accessDoor("core.access.createPrincipal", request, (result) =>
      TokenGrantSchema.parse(result),
    );
  }

  /**
   * Mints an attenuated credential (`core.access.mint`) no broader than this client's current
   * authority. Minting for another existing human principal requires a live credential this actor
   * issued within its current scope; historical issuance alone is not principal ownership.
   * Root and explicit self administration remain exceptions owned by the server.
   */
  async mintToken(input: MintTokenRequest): Promise<AccessOutcome<TokenGrant>> {
    const request = MintTokenRequestSchema.parse(input);
    return this.accessDoor("core.access.mint", request, (result) => TokenGrantSchema.parse(result));
  }

  /**
   * Withdraws the credentials this caller may manage for a principal (`core.access.revoke`)
   * and answers HOW MANY died — zero is a success, not a refusal. Root withdraws the complete
   * set; self withdrawal remains explicit and scope-consistent; ordinary withdrawal for another
   * principal reaches only credentials this actor issued within its current scope. Registered
   * Agents retain their full credential and Run-subtree cutoff. The server's revocation fence
   * closes only sockets holding credentials actually withdrawn.
   */
  async revokeToken(principalId: string): Promise<AccessOutcome<RevokeResult>> {
    const request = RevokeRequestSchema.parse({ principalId });
    return this.accessDoor("core.access.revoke", request, (result) =>
      RevokeResultSchema.parse(result),
    );
  }
  /** Pauses future requests for one non-owner principal without revoking its credentials. */
  async pauseAccess(principalId: string): Promise<AccessOutcome<PrincipalAccessPauseResult>> {
    const request = PrincipalAccessPauseRequestSchema.parse({ principalId });
    return this.accessDoor("core.access.pause", request, (result) =>
      PrincipalAccessPauseResultSchema.parse(result),
    );
  }

  /** Resumes the same principal credentials without requiring a new authentication. */
  async resumeAccess(principalId: string): Promise<AccessOutcome<PrincipalAccessPauseResult>> {
    const request = PrincipalAccessPauseRequestSchema.parse({ principalId });
    return this.accessDoor("core.access.resume", request, (result) =>
      PrincipalAccessPauseResultSchema.parse(result),
    );
  }

  /**
   * AUTHORIZED LIVE CREDENTIAL INVENTORY (`core.access.listCredentials`, ADR 0019 §3), a
   * workspace-scoped door. Root receives the complete live inventory; self remains explicit;
   * other ordinary principals expose only this actor's live issuance. Registered Agent inventories
   * match their full credential/Run cutoff. Native service rows remain inspection-only.
   *
   * The server is authoritative. Consumers must not reconstruct authorization filtering, and
   * ordinary legacy credentials without issuer provenance do not become another actor's property.
   */
  async credentials(): Promise<AccessOutcome<CredentialsResponse>> {
    return this.accessDoor("core.access.listCredentials", {}, (result) =>
      CredentialsResponseSchema.parse(result),
    );
  }

  /**
   * Withdraws an enrolled machine's credential (`core.machines.revoke`, ADR 0019 §3) and
   * answers HOW MANY died — zero is a success, exactly as it is for {@link revokeToken}.
   *
   * The inventory row survives: withdrawing a credential and forgetting a box are different
   * verbs, and re-enrolling the same name with `rotateToken: true` is how a machine comes
   * back. The machine's live socket is severed by the same fence a principal's is.
   */
  async revokeMachine(machineId: string): Promise<AccessOutcome<RevokeResult>> {
    const request = RevokeMachineRequestSchema.parse({ machineId });
    return this.accessDoor("core.machines.revoke", request, (result) =>
      RevokeResultSchema.parse(result),
    );
  }

  /**
   * Closes (`draining: true`) or reopens (`draining: false`) an enrolled machine's terminal
   * admission (`core.machines.drain`, issue #278) and answers what its PTY OWNER holds after
   * applying that state: the owner's identity and every live terminal id, complete behind
   * every create the hub had sent. The hub closes admission before it asks, so once this
   * resolves with `draining: true` no terminal can be born on the machine until it is
   * reopened here — which is the only way it reopens.
   *
   * A denial means the owner could not answer — offline, an agent that names no owner, a
   * timeout — and the admission the hub set is exactly what was asked for regardless: a
   * refused drain is a machine whose state is unknown AND which admits nothing new, never a
   * machine left open. Unknown is not safe; treat a denial as "do not replace".
   */
  async drainMachine(
    machineId: string,
    draining: boolean,
  ): Promise<AccessOutcome<MachineDrainStatus>> {
    const request = DrainMachineRequestSchema.parse({ machineId, draining });
    return this.accessDoor("core.machines.drain", request, (result) =>
      MachineDrainStatusSchema.parse(result),
    );
  }

  sendGesture(gesture: Gesture): void {
    this.send({ type: "gesture", ...gesture });
  }

  outboxSize(): number {
    return this.outbox.length;
  }

  sendPresence(payload: PresencePayload): void {
    this.send({ type: "presence", payload });
  }

  sendCursor(x: number, y: number): void {
    this.send({ type: "cursor", x, y });
  }

  requestResync(): void {
    this.send({ type: "resync_request" });
  }

  /**
   * Opens a terminal and resolves with its terminal once the server confirms.
   *
   * A terminal is born into a COMPOSITION of its own, and `terminal.containerId` names it. Under
   * the default placement the caller is a canvas, so `elementId` is both the correlation
   * token and the id the caller authors its own element under — a `portal` onto
   * `terminal.containerId`, never a terminal element, because a canvas only ever references the
   * container a terminal lives in. `placement: "tile"` is how a TILED container births
   * one: that container IS the home, so the server writes the tile leaf first. Supply both
   * `cols`/`rows` when this caller already owns a measured or virtual viewport, or omit both
   * to wait for the first viewer's `terminal_resize`. The caller authors nothing; resolving
   * means the measured PTY is running.
   *
   * `program` names what the PTY execs instead of the machine's shell, and `env` is merged
   * under the fixed `MANIFOLD_*` keys (issue #192). Both ride the one `terminal_open` frame,
   * and the server hands them to `core.terminals.open` before any create: a policy denial
   * (`forbidden`, the door's message) refuses the program before a machine hears of it. A
   * machine whose agent predates programs rejects with `unsupported`; a program the machine
   * cannot exec is a `conflict` from the agent's `create_error`.
   */
  openTerminal(
    opts: {
      elementId: string;
      machineId?: string;
      program?: TerminalProgram;
      env?: TerminalEnv;
      cwd?: string;
      timeoutMs?: number;
    } & (
      { runtime: TerminalRuntime; program?: never; env?: never; cwd?: never } | { runtime?: never }
    ) &
      (
        | { placement: "tile"; cols?: never; rows?: never }
        | { placement: "tile"; cols: number; rows: number }
        | { placement?: never; cols: number; rows: number }
      ),
  ): Promise<TerminalInfo> {
    const { promise, resolve, reject } = Promise.withResolvers<TerminalInfo>();
    const settle = (outcome: () => void): void => {
      clearTimeout(timer);
      offOpened();
      offError();
      offStatus();
      outcome();
    };
    const timer = setTimeout(
      () => settle(() => reject(new Error("terminal_open timed out"))),
      opts.timeoutMs ?? 15_000,
    );
    const offOpened = this.on("terminal_opened", (msg) => {
      // A server-placed reply echoes the token as `ref`, because its `elementId` is a
      // tile id this caller never chose.
      if ((msg.ref ?? msg.elementId) !== opts.elementId) return;
      settle(() => resolve(msg.terminal));
    });
    const offError = this.on("error", (msg) => {
      if (msg.ref !== opts.elementId) return;
      settle(() => reject(new Error(`terminal_open failed: ${msg.message ?? msg.code}`)));
    });
    const offStatus = this.on("status", (status: ConnectionStatus) => {
      if (status === "closed") {
        settle(() =>
          reject(this.closeError ?? new Error("terminal closed before terminal opened")),
        );
      }
    });
    this.send({
      type: "terminal_open",
      elementId: opts.elementId,
      ...(opts.cols === undefined ? {} : { cols: opts.cols }),
      ...(opts.rows === undefined ? {} : { rows: opts.rows }),
      ...(opts.cwd !== undefined ? { cwd: opts.cwd } : {}),
      ...(opts.machineId !== undefined ? { machineId: opts.machineId } : {}),
      ...(opts.placement !== undefined ? { placement: opts.placement } : {}),
      ...(opts.program !== undefined ? { program: opts.program } : {}),
      ...(opts.env !== undefined ? { env: opts.env } : {}),
      ...(opts.runtime !== undefined ? { runtime: opts.runtime } : {}),
    });
    return promise;
  }

  /**
   * Starts one view's delivery: the server answers with a fresh `terminal_snapshot` for this
   * exact `(terminalId, viewportId)` and then that view's ordered tail, every frame carrying
   * `viewportId`, `deliveryId` and `deliverySeq`. Two views of one terminal on this client are
   * independent deliveries, so each mounted parser names its own `viewportId`; there is no
   * shared default. Every attach re-establishes the view with a fresh snapshot, and the
   * refcount is per exact pair, so the last `detachTerminal` of that pair releases it. Held
   * pairs re-attach by themselves after a reconnect and after a restart.
   */
  attachTerminal(terminalId: string, viewportId: string): void {
    let views = this.attachCounts.get(terminalId);
    if (views === undefined) {
      views = new Map();
      this.attachCounts.set(terminalId, views);
    }
    views.set(viewportId, (views.get(viewportId) ?? 0) + 1);
    this.send({ type: "terminal_attach", terminalId, viewportId });
  }

  /** Releases one hold of exactly this view; sibling views keep streaming. */
  detachTerminal(terminalId: string, viewportId: string): void {
    const views = this.attachCounts.get(terminalId);
    const current = views?.get(viewportId) ?? 0;
    if (views === undefined || current === 0) return;
    if (current > 1) {
      views.set(viewportId, current - 1);
      return;
    }
    views.delete(viewportId);
    if (views.size === 0) this.attachCounts.delete(terminalId);
    this.send({ type: "terminal_detach", terminalId, viewportId });
  }

  /**
   * Credits this view's parser with having COMPLETED every frame of `deliveryId` through
   * `deliverySeq` — the snapshot's replay included. Call it from the parser's own completion,
   * never on receipt: the server paces this view by these acknowledgements alone. It is
   * cumulative, sent only while open and never replayed, since an incarnation never outlives
   * its connection; a stale, duplicate or future acknowledgement grants nothing.
   */
  ackTerminal(
    terminalId: string,
    viewportId: string,
    deliveryId: string,
    deliverySeq: number,
  ): void {
    if (this.status !== "open") return;
    this.send({ type: "terminal_ack", terminalId, viewportId, deliveryId, deliverySeq });
  }

  sendTerminalInput(terminalId: string, data: string | Uint8Array): void {
    const b64 = typeof data === "string" ? textToBase64(data) : bytesToBase64(data);
    this.send({ type: "terminal_input", terminalId, data: b64 });
  }

  /**
   * Publishes desired geometry for one active attached view, not the applied shared grid.
   * Renew at TERMINAL_VIEWPORT_REFRESH_MS while foreground and visible; an unrenewed
   * measurement expires after TERMINAL_VIEWPORT_LEASE_MS. The three-argument public call
   * represents this client's single virtual "sdk" viewport, which the server accepts only
   * while that exact view is attached: pair it with `attachTerminal(terminalId, "sdk")`.
   * Mounted views supply the id they attached.
   */
  resizeTerminal(terminalId: string, cols: number, rows: number, viewportId = "sdk"): void {
    if (this.status !== "open") return;
    this.send({ type: "terminal_resize", terminalId, viewportId, viewport: { cols, rows } });
  }

  /** Immediately retires one view without detaching sibling renderers on this channel. */
  releaseTerminalViewport(terminalId: string, viewportId = "sdk"): void {
    if (this.status !== "open") return;
    this.send({ type: "terminal_resize", terminalId, viewportId, viewport: null });
  }

  takeTerminal(terminalId: string): void {
    this.send({ type: "terminal_take", terminalId });
  }

  killTerminal(terminalId: string): void {
    this.send({ type: "terminal_kill", terminalId });
  }
}
